import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

import { Tabs, Tab, CircularProgress, Typography, Box, Button, Tooltip, Theme } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import DateRangeIcon from "@mui/icons-material/DateRange";

/* region Hooks */
import { useAuth } from "@hooks/useAuth";
import { useSeenVersion } from "@hooks/useSeenVersion";
/* endregion Hooks */

/* region Languages */
import { CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
/* endregion Languages */
/* region Pages */
import Office from "@pages/Office";
/* endregion Pages */

import MarkdownRenderer from "./components/MarkdownRenderer";
import TableOfContents from "./components/TableOfContents";
import ChangelogSidebar from "./components/ChangelogSidebar";
import ChangelogVersionView, { fill } from "./components/ChangelogVersionView";
import VersionDetailsDialog from "./components/VersionDetailsDialog";
import VersionRangeDialog from "./components/VersionRangeDialog";
import { fetchDetailedLines, versionRangeHash } from "./components/versionDetails";
import { parseChangelog, versionLine } from "./changelog";

const MANUAL_URL = `${process.env.PUBLIC_URL}/docs/MANUAL.md`;
const CHANGELOG_URL = `${process.env.PUBLIC_URL}/docs/CHANGELOG.md`;

const pageContainerSx = {
  p: 2.5,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2
};

const contentAreaSx = (theme: Theme) => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper
});

// A hairline that stays invisible until the pointer is over the column. Chromium ignores the
// ::-webkit-scrollbar rules wherever scrollbar-width/scrollbar-color are set, so those stay out.
const delicateScrollbar = {
  "&::-webkit-scrollbar": { width: 6, height: 6 },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "transparent", borderRadius: 10 },
  "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "rgba(100, 116, 139, 0.18)" },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "rgba(100, 116, 139, 0.35)" }
};

const sidebarSx = (theme: Theme) => ({
  width: 280,
  minWidth: 280,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: "auto",
  p: 2,
  ...delicateScrollbar
});

const contentSx = { flex: 1, minWidth: 0, overflowY: "auto", p: 4, ...delicateScrollbar };

const centerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: 2,
  height: 400,
  width: "100%"
};

interface ChangelogContentProps {
  content: string;
  /** Version lines the details page documents; null hides "Novidades em detalhe" and "Resumo de versões" */
  detailedLines: string[] | null;
  /** Version to start on; the newest by default */
  initialVersion?: string;
  /** Opens Novidades em detalhe of the start version right away, when that version line has details */
  openDetails?: boolean;
}

/**
 * Renders the currently selected changelog version as KPIs and one table per kind of change, with
 * "Novidades em detalhe" and "Resumo de versões" when the details page is published. Listens for the
 * `changelog-version-select` event dispatched by the version sidebar.
 */
const ChangelogContent: React.FC<ChangelogContentProps> = ({ content, detailedLines, initialVersion, openDetails }) => {
  const { t } = useTranslation();
  const versions = parseChangelog(content);
  const [selectedVersion, setSelectedVersion] = useState(
    (initialVersion && versions.some((version) => version.version === initialVersion) ? initialVersion : versions[0]?.version) || ""
  );
  const [details, setDetails] = useState<{ title: string; hash: string } | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const detailsOpened = useRef(false);

  // the banner's "Ver novidades" lands straight on the version in detail, once
  useEffect(() => {
    if (!openDetails || detailsOpened.current || !detailedLines) return;

    detailsOpened.current = true;

    if (detailedLines.includes(versionLine(selectedVersion))) {
      setDetails({
        title: fill(t(CustomerMessages.docsVersionDetailsTitle), { VERSION: selectedVersion }),
        hash: versionRangeHash(selectedVersion, selectedVersion)
      });
    }
  }, [openDetails, detailedLines, selectedVersion, t]);

  useEffect(() => {
    const handleVersionChange = (event: Event) => {
      setSelectedVersion((event as CustomEvent<string>).detail);
    };

    window.addEventListener("changelog-version-select", handleVersionChange);

    return () => window.removeEventListener("changelog-version-select", handleVersionChange);
  }, []);

  if (versions.length === 0) {
    return <Typography color="textSecondary">{t(CustomerMessages.docsNoVersions)}</Typography>;
  }

  const current = versions.find((version) => version.version === selectedVersion) || versions[0];
  const line = versionLine(current.version);
  const documented = !!detailedLines?.includes(line);
  const openRange = (from: string, to: string) => {
    setRangeOpen(false);
    setDetails({
      title: from === to
        ? fill(t(CustomerMessages.docsVersionDetailsTitle), { VERSION: from })
        : fill(t(CustomerMessages.docsVersionRangeTitle), { FROM: from, TO: to }),
      hash: versionRangeHash(from, to)
    });
  };

  const actions = detailedLines ? (
    <>
      <Button variant="outlined" size="small" startIcon={<DateRangeIcon />} onClick={() => setRangeOpen(true)}>
        {t(CustomerMessages.docsVersionRange)}
      </Button>
      <Tooltip title={documented ? "" : fill(t(CustomerMessages.docsVersionNoDetails), { LINE: line })}>
        <span>
          <Button
            variant="contained"
            size="small"
            startIcon={<NewReleasesIcon />}
            disabled={!documented}
            onClick={() => openRange(current.version, current.version)}
          >
            {t(CustomerMessages.docsVersionDetails)}
          </Button>
        </span>
      </Tooltip>
    </>
  ) : undefined;

  const heading = current.date ? `v${current.version} — ${current.date}` : `v${current.version}`;
  const hasItems = current.sections.some((section) => section.items.length > 0);

  return (
    <>
      {hasItems ? (
        <ChangelogVersionView entry={current} actions={actions} />
      ) : (
        <>
          {actions && <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>{actions}</Box>}
          <MarkdownRenderer content={`# ${heading}\n\n${current.body}`} />
        </>
      )}
      {detailedLines && (
        <>
          <VersionDetailsDialog open={!!details} title={details?.title || ""} hash={details?.hash || ""} onClose={() => setDetails(null)} />
          <VersionRangeDialog
            open={rangeOpen}
            versions={versions.map((version) => version.version)}
            initial={current.version}
            onClose={() => setRangeOpen(false)}
            onConfirm={openRange}
          />
        </>
      )}
    </>
  );
};

ChangelogContent.defaultProps = {
  initialVersion: undefined,
  openDetails: false
};

/**
 * In-app documentation center: a "Manual" tab (scroll-spy index + rendered
 * manual) and a "Novidades" tab (version list + per-version changelog).
 * The header stays fixed while only the content card scrolls.
 */
const Docs: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [detailedLines, setDetailedLines] = useState<string[] | null>(null);
  const mounted = useRef(true);
  const location = useLocation();
  // the new-version banner links here with ?novidades=<version>
  const requestedVersion = new URLSearchParams(location.search).get("novidades") || undefined;
  const { markSeen } = useSeenVersion(user?.email);

  const [tab, setTab] = useState(requestedVersion ? 1 : 0);
  const [manual, setManual] = useState("");
  const [changelog, setChangelog] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [manualScrollEl, setManualScrollEl] = useState<HTMLDivElement | null>(null);

  const manualScrollRef = useCallback((node: HTMLDivElement | null) => {
    setManualScrollEl(node);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError(false);

        const [manualRes, changelogRes] = await Promise.all([
          fetch(MANUAL_URL, { signal: controller.signal }),
          fetch(CHANGELOG_URL, { signal: controller.signal })
        ]);

        if (manualRes.ok) setManual(await manualRes.text());
        if (changelogRes.ok) setChangelog(await changelogRes.text());
        if (!manualRes.ok && !changelogRes.ok) setError(true);
      } catch (fetchError) {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  // opening Novidades counts as seeing the new version: the banner and the "Novo" badge go away
  useEffect(() => {
    if (tab === 1) markSeen();
  }, [tab, markSeen]);

  // "Novidades em detalhe" reads the details page published with this build, for every user
  useEffect(() => {
    fetchDetailedLines().then((lines) => {
      if (mounted.current) setDetailedLines(lines);
    });
  }, []);

  return (
    <Office showSelectClient={false} fillContent>
      <Box sx={pageContainerSx}>
        <Box sx={pageHeaderSx}>
          <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
            {t(CustomerMessages.applicationDocs)}
          </Typography>
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              icon={<MenuBookIcon fontSize="small" />}
              iconPosition="start"
              label={t(CustomerMessages.docsTabManual)}
              sx={{ textTransform: "none", minHeight: 48 }}
            />
            <Tab
              icon={<NewReleasesIcon fontSize="small" />}
              iconPosition="start"
              label={t(CustomerMessages.docsTabChangelog)}
              sx={{ textTransform: "none", minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {loading && (
          <Box sx={centerSx}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box sx={centerSx}>
            <Typography color="textSecondary">{t(CustomerMessages.docsLoadError)}</Typography>
          </Box>
        )}

        {!loading && !error && tab === 0 && (
          <Box sx={contentAreaSx}>
            <Box sx={sidebarSx}>
              <TableOfContents content={manual} scrollContainer={manualScrollEl} />
            </Box>
            <Box sx={contentSx} ref={manualScrollRef}>
              <MarkdownRenderer content={manual} />
            </Box>
          </Box>
        )}

        {!loading && !error && tab === 1 && (
          <Box sx={contentAreaSx}>
            <Box sx={sidebarSx}>
              <ChangelogSidebar content={changelog} initialVersion={requestedVersion} />
            </Box>
            <Box sx={contentSx}>
              <ChangelogContent
                content={changelog}
                detailedLines={detailedLines}
                initialVersion={requestedVersion}
                openDetails={!!requestedVersion}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Office>
  );
};

export default Docs;
